import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { LabelToId } from 'src/app/common/input-utils'
import { Datum } from 'src/app/models/common/datum'

const ROVING_KEYS = [
  'ArrowRight',
  'ArrowDown',
  'ArrowLeft',
  'ArrowUp',
  'Home',
  'End',
]

// Single-select tile picker (DESIGN.md "Tile" spec) — real <button role="radio"> options inside a
// role="radiogroup" container, used wherever the mockup shows a tile-based single choice (Step 2's
// Plan management, Step 1's retrofitted Plan type). Not literally reusing Material/mat-radio-group —
// see story e8-2 Dev Notes for why this is a new control rather than category-tile/category-pill.
@Component({
  selector: 'app-tile-group',
  templateUrl: './tile-group.component.html',
  styleUrls: ['./tile-group.component.scss'],
  standalone: false,
})
export class TileGroupComponent implements OnInit {
  @Input() control: FormControl
  @Input() options: Datum[] = []
  @Input() label: string
  @Input() required: boolean = false
  @Input() errorText: string

  @ViewChildren('tileButton') tileButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >

  id: string
  errorId: string

  ngOnInit(): void {
    this.id = LabelToId(this.label)
    this.errorId = `${this.id}-error`
  }

  get isInvalid(): boolean {
    return (
      this.required &&
      this.control?.invalid === true &&
      this.control?.touched === true
    )
  }

  isSelected(value: string): boolean {
    return this.control?.value === value
  }

  selectOption(value: string): void {
    this.control?.setValue(value)
    this.control?.markAsTouched()
  }

  // Roving-tabindex per the WAI-ARIA radiogroup pattern: only the selected option (or the first
  // option when nothing is selected yet) is a Tab stop — the rest are reached via arrow keys.
  tabIndexFor(value: string, index: number): number {
    if (this.isSelected(value)) {
      return 0
    }
    const hasSelection = this.options.some((option) =>
      this.isSelected(option.value),
    )
    return !hasSelection && index === 0 ? 0 : -1
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    if (!ROVING_KEYS.includes(event.key)) {
      return
    }
    event.preventDefault()

    const lastIndex = this.options.length - 1
    let nextIndex = index
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = index === lastIndex ? 0 : index + 1
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = index === 0 ? lastIndex : index - 1
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = lastIndex
    }

    this.selectOption(this.options[nextIndex].value)
    this.tileButtons?.toArray()[nextIndex]?.nativeElement.focus()
  }
}
